import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { Loader, Center } from '@/components/ui';

// Eager load dashboard for instant access
import BudgetsDashboard from './dashboard';

// Lazy load all other pages
const BudgetListPage = lazy(() => import('./list'));
const CreateBudgetPage = lazy(() => import('./create'));
const BudgetDetailPage = lazy(() => import('./view/detail'));
const ApprovalQueuePage = lazy(() => import('./approvals'));
const AlertsPage = lazy(() => import('./alerts'));
const UnbudgetedPage = lazy(() => import('./unbudgeted'));
const ChangeRequestQueuePage = lazy(() => import('./change-requests'));
const BudgetAnalyticsPage = lazy(() => import('./analytics'));
const CombinedAnalyticsPage = lazy(() => import('./analytics/combined'));
const BudgetAiInsightsPage = lazy(() => import('./ai-insights'));

// v3 — foresight, exploration and reporting
const ForecastPage = lazy(() => import('./forecast'));
const TrendsPage = lazy(() => import('./trends'));
const CashflowPage = lazy(() => import('./cashflow'));
const VariancePage = lazy(() => import('./variance'));
const AnomaliesPage = lazy(() => import('./anomalies'));
const LedgerPage = lazy(() => import('./ledger'));
const ReportsPage = lazy(() => import('./reports'));

// Admin pages
const AdminSettingsPage = lazy(() => import('./admin/settings'));
const AdminLookupsPage = lazy(() => import('./admin/lookups'));
const LookupCategoryPage = lazy(() => import('./admin/lookups/[category]'));
const LookupManagementPage = lazy(() => import('./admin/lookups/[category]/[lookup]'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

const APPROVER_ROLES = ['Admin', 'BudgetAdmin'];
/**
 * Managers get forecasting and reports for the budgets they own or manage — the endpoints
 * scope them server-side. A department manager learning their budget runs out three weeks
 * early is the most valuable thing in this release; putting it behind an admin role would
 * waste it.
 */
const MANAGER_ROLES = ['Admin', 'BudgetAdmin', 'BudgetManager'];

/**
 * Budget Management Routes
 * Complete routing configuration for the Budgets domain
 */
export default function BudgetManagementRoutes() {
  return (
    <MainLayout domainKey="budgets">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<BudgetsDashboard />} />

          {/* Budgets */}
          <Route path="/list" element={<BudgetListPage />} />
          <Route path="/create" element={<CreateBudgetPage />} />

          {/* Budget Detail View */}
          <Route path="/view/:id" element={<BudgetDetailPage />} />

          {/* Approver Routes - Protected */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <ApprovalQueuePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <AlertsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/unbudgeted"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <UnbudgetedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-requests"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <ChangeRequestQueuePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <BudgetAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/combined"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <CombinedAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-insights/*"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <BudgetAiInsightsPage />
              </ProtectedRoute>
            }
          />

          {/* v3 — foresight, exploration, reporting */}
          <Route
            path="/forecast"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <ForecastPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trends"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <TrendsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <CashflowPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/variance"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <VariancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/anomalies"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <AnomaliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ledger"
            element={
              <ProtectedRoute requiredRoles={APPROVER_ROLES}>
                <LedgerPage />
              </ProtectedRoute>
            }
          />
          {/* Managers build reports about their own budgets, so this is not admin-only. */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute requiredRoles={MANAGER_ROLES}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - Protected */}
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminLookupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups/:category"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LookupCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups/:category/:lookup"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <LookupManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/budgets" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
